/**
 * Rollen und Rechte (Quelle der Wahrheit für API, Web und App).
 * Entspricht der Rechte-Matrix in docs/01-ARCHITEKTUR.md. In der Datenbank stehen nur die
 * Rollen-Zuweisungen, die Zuordnung Rolle -> Rechte steht hier und ist vollständig getestet.
 *
 * WICHTIG (Bereichsbezug): Rollen gelten nur für genau eine Organisation bzw. einen Basar.
 * Der Aufrufer (API) übergibt nur die Rollen, die für die **angefragte** Organisation und den
 * **angefragten** Basar aufgelöst wurden. Ein Manager von Basar A darf Basar B nie verwalten.
 * Nicht abgebildet, weil kein Organisations- oder Basar-Recht: `platform_admin` (Plattform-Ebene).
 * Eigentumsprüfung („nur eigene Daten“, z. B. eigener Verkäufer, eigene Abrechnung) gehört in die
 * API, dieses Paket sagt nur, ob die Rolle das Recht grundsätzlich hat.
 * Berichte mit personenbezogenen Spalten (Namen, IBAN, Telefon) zusätzlich an
 * `event.contacts.view` bzw. `event.sellers.bank.view` knüpfen, nicht nur an `event.reports.view`.
 */

export const ORG_ROLES = ['owner', 'admin', 'member'] as const;
export type OrgRole = (typeof ORG_ROLES)[number];

export const EVENT_ROLES = ['manager', 'intake', 'cashier', 'helper', 'seller'] as const;
export type EventRole = (typeof EVENT_ROLES)[number];

export const ROLES = [...ORG_ROLES, ...EVENT_ROLES] as const;
export type Role = OrgRole | EventRole;

export const PERMISSIONS = [
  'org.view',
  'org.settings.edit',
  'org.delete',
  'org.members.manage',
  'org.billing.manage',
  'org.events.create',
  'org.reports.export',
  'org.mailTemplates.manage', // eigene Mailvorlagen der Organisation (Betreff und Text ausgewählter Mails)
  'org.messages.broadcast', // Rundschreiben an Inhaber/Verwalter bzw. Mitglieder der Organisation
  'event.view',
  'event.manage',
  'event.sellers.approve',
  'event.team.manage',
  'event.items.receive',
  'event.returns.sort',
  'event.items.inspect',
  'event.entry.check', // Einlass-Pass prüfen (Station „Einlass“, ohne Beträge; beim Scan ohne Namen, Liste mit Kurzname nur für Personen im Schichtplan, sonst nur Verkäufernummer, Name und Nummer nie zusammen ohne event.contacts.view, W3-Bonus-b)
  'event.sales.create',
  'event.sales.void.own',
  'event.sales.void.any',
  'event.settlement.run',
  'event.payouts.approve',
  'event.cash.count.own', // Bargeldabgleich (Wechselgeld, Entnahme, Zählung) der EIGENEN Kasse erfassen (X5)
  'event.cash.count.any', // Bargeldabgleich aller Kassen des Basars ansehen und erfassen (X5)
  'event.sellers.bank.view',
  'event.contacts.view',
  'event.reports.view',
  'event.reports.export',
  'event.messages.manage', // Team-Posteingang des Basars lesen/beantworten, Einzelnachricht an Personen des Basars
  'event.messages.broadcast', // Rundschreiben an Zielgruppen
  'event.news.publish', // Neuigkeiten anlegen, ändern, anheften, löschen
  'seller.self.manage',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

const MANAGER: readonly Permission[] = [
  'event.manage',
  'event.sellers.approve',
  'event.team.manage',
  'event.items.receive',
  'event.returns.sort',
  'event.items.inspect',
  'event.entry.check',
  'event.sales.void.any',
  'event.settlement.run',
  'event.payouts.approve',
  'event.cash.count.own',
  'event.cash.count.any',
  'event.sellers.bank.view',
  'event.contacts.view',
  'event.reports.view',
  'event.reports.export',
  'event.messages.manage',
  'event.messages.broadcast',
  'event.news.publish',
];

const RAW: Readonly<Record<Role, readonly Permission[]>> = {
  // Owner und Admin erben die Rechte des Managers, verkaufen aber nicht an der Kasse.
  owner: [
    'org.view',
    'org.settings.edit',
    'org.delete',
    'org.members.manage',
    'org.billing.manage',
    'org.events.create',
    'org.reports.export',
    'org.mailTemplates.manage',
    'org.messages.broadcast',
    'event.view',
    ...MANAGER,
  ],
  admin: [
    'org.view',
    'org.members.manage',
    'org.billing.manage',
    'org.events.create',
    'org.reports.export',
    'org.mailTemplates.manage',
    'org.messages.broadcast',
    'event.view',
    ...MANAGER,
  ],
  member: ['org.view', 'event.view', 'event.reports.view'],
  manager: ['event.view', ...MANAGER, 'event.sales.create', 'event.sales.void.own'],
  intake: [
    'event.view',
    'event.items.receive',
    'event.returns.sort',
    'event.items.inspect',
    'event.entry.check',
  ],
  cashier: [
    'event.view',
    'event.sales.create',
    'event.sales.void.own',
    'event.items.inspect',
    'event.cash.count.own',
  ],
  helper: ['event.view', 'event.returns.sort', 'event.items.inspect', 'event.entry.check'],
  seller: ['seller.self.manage'],
};

/** Einmal vorberechnet und eingefroren. */
const ROLE_PERMISSIONS: Readonly<Record<Role, ReadonlySet<Permission>>> = Object.freeze({
  owner: new Set(RAW.owner),
  admin: new Set(RAW.admin),
  member: new Set(RAW.member),
  manager: new Set(RAW.manager),
  intake: new Set(RAW.intake),
  cashier: new Set(RAW.cashier),
  helper: new Set(RAW.helper),
  seller: new Set(RAW.seller),
});

export function isRole(value: string): value is Role {
  return (ROLES as readonly string[]).includes(value);
}

/** Rechte einer einzelnen Rolle. */
export function permissionsOf(role: Role): ReadonlySet<Permission> {
  return ROLE_PERMISSIONS[role];
}

/** Rollen einer Person **für genau eine Organisation und einen Basar** (siehe Kopfkommentar). */
export interface RoleContext {
  readonly orgRoles: readonly OrgRole[];
  readonly eventRoles: readonly EventRole[];
}

/** Darf eine Person mit diesen (bereits auf Organisation/Basar aufgelösten) Rollen das Recht? */
export function can(context: RoleContext, permission: Permission): boolean {
  for (const role of context.orgRoles) {
    if (ROLE_PERMISSIONS[role].has(permission)) return true;
  }
  for (const role of context.eventRoles) {
    if (ROLE_PERMISSIONS[role].has(permission)) return true;
  }
  return false;
}
